import { useState } from 'react';
import { Button, Dialog, Field, Textarea } from '@zt/ui';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { OrderDetail } from '../lib/types';

/**
 * Ön provizyon (F5B-14, ADR-070): kartta bloke tutar, son tahsil tarihi; "Tahsil et" ve "Provizyonu bırak".
 * Tahsilat kargoya verirken kendiliğinden de yapılır.
 */
export function AuthorizationBox({ order }: { order: OrderDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [open, setOpen] = useState<'capture' | 'release' | null>(null);
  const a = order.authorization;
  if (!a) return null;
  const partial = order.totalMinor < a.amountMinor;
  return (
    <div
      className={
        a.expiringSoon
          ? 'mb-3 rounded-sm border border-amber/40 bg-amber-tint px-3 py-2 text-sm text-ink'
          : 'mb-3 rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm text-ink'
      }
      role="status"
    >
      <p className="font-medium">{t('admin.preauth.held', { amount: money(a.amountMinor, a.currency) })}</p>
      <p className={a.expiringSoon ? 'text-amber' : 'text-ink-2'}>
        {t('admin.preauth.deadline', { date: formatDate(a.expiresAt, true), days: a.daysLeft })}
        {a.expiringSoon && ` ${t('admin.preauth.expiringSoon')}`}
      </p>
      {partial && (
        <p className="text-ink-2">
          {t('admin.preauth.partial', {
            amount: money(order.totalMinor, order.currency),
            rest: money(a.amountMinor - order.totalMinor, order.currency),
          })}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {can('payments.payment.capture') && (
          <>
            <Button size="sm" variant="primary" onClick={() => setOpen('capture')}>
              {t('admin.preauth.capture')}
            </Button>
            <HelpTip k="orders.preauth_capture" />
          </>
        )}
        {can('orders.refund.approve') && (
          <>
            <Button size="sm" variant="danger" onClick={() => setOpen('release')}>
              {t('admin.preauth.release')}
            </Button>
            <HelpTip k="orders.preauth_release" />
          </>
        )}
      </div>
      {open === 'capture' && <CaptureDialog order={order} onClose={() => setOpen(null)} />}
      {open === 'release' && <ReleaseDialog order={order} onClose={() => setOpen(null)} />}
    </div>
  );
}

function CaptureDialog({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const a = order.authorization!;
  const run = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${order.id}/preauth/capture`,
        { note: note.trim() || undefined },
        { idempotencyKey: idem, ifMatch: order.version },
      ),
    {
      invalidate: [['order', order.id], ['orders'], ['payments']],
      success: t('admin.preauth.captured'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.preauth.captureTitle')}
      description={t('admin.preauth.captureLead', {
        amount: money(order.totalMinor, order.currency),
        held: money(a.amountMinor, a.currency),
      })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={run.isPending} onClick={() => run.mutate(undefined)}>
            {t('admin.preauth.capture')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {run.error && !run.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {run.error.title}
          </p>
        )}
        <Field label={t('admin.orders.statusNote')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function ReleaseDialog({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const t = useT();
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const run = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${order.id}/preauth/release`,
        { note: note.trim() },
        { idempotencyKey: idem, ifMatch: order.version },
      ),
    {
      invalidate: [['order', order.id], ['orders'], ['payments']],
      success: t('admin.preauth.released'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.preauth.releaseTitle')}
      description={t('admin.preauth.releaseLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={run.isPending}
            disabled={note.trim().length < 3}
            onClick={() => run.mutate(undefined)}
          >
            {t('admin.preauth.release')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {run.error && !run.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {run.error.title}
          </p>
        )}
        <Field label={t('admin.preauth.releaseNote')} required error={fieldError(run.error, 'note')}>
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
